import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormTreeSelect,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { treeOptionPropType } from '../../../common/prop-types'

import { useUpdateDishTypeIgnoredDetailsMutation } from '../../hooks'
import { dishTypePropType } from '../../prop-types'
import { setDishTypePageIsDirty } from '../../redux/actions'

const DishTypeIgnoredDetailsForm = ({ dishType, courseTypeOptions }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.UPDATE_IGNORED_DETAILS,
  ])

  const [
    updateDishTypeIgnoredDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDishTypeIgnoredDetailsMutation()

  const isIgnoredRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value && !isNil(getFieldValue('courseTypeId'))) {
            return Promise.reject(
              new Error(
                'Must be ignored if an ignored course type is selected',
              ),
            )
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const courseTypeIdRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (isNil(value) && getFieldValue('isIgnored') === true) {
            return Promise.reject(
              new Error(
                'Must select an ignored course type if dish type is ignored',
              ),
            )
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  const handleSaveData = useCallback(
    data => updateDishTypeIgnoredDetails(dishType.id, data),
    [dishType.id, updateDishTypeIgnoredDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [dishType.isIgnored, dishType.courseTypeId, resetForm])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isIgnoredDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={dishType}
      name="ignoredForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={12}>
          <FormCheckbox
            label="Ignored"
            name="isIgnored"
            rules={isIgnoredRules}
            disabled={!isFormEnabled}
          />
          <FormTreeSelect
            form={form}
            rules={courseTypeIdRules}
            label="Same Ignored Course Type"
            labelAbove
            allowClear
            showSearch
            name="courseTypeId"
            treeData={courseTypeOptions}
            placeholder="Select Course Type..."
            disabled={!isFormEnabled}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DISH_TYPE.UPDATE_IGNORED_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DishTypeIgnoredDetailsForm.propTypes = {
  courseTypeOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  dishType: dishTypePropType.isRequired,
}

export default DishTypeIgnoredDetailsForm
